import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { useAccount } from './useAccount'
import { API_BASE_URL } from '../constants/system'
import type { CodingModelsResponse } from '../types/codingModels'

/**
 * Which models this job can actually code with.
 *
 * The picker used to be a hardcoded array in PipelinePromptSettingsStep. It
 * drifted from the catalog: it offered five models with no credentials in this
 * environment and omitted every configured Anthropic lane. Choosing a dead one
 * failed at dispatch, long after the choice. This asks the server instead, and
 * the server answers from llm.Available — the same check llm.New applies.
 *
 * Unlike the lane census this is not live data: the catalog only changes on
 * deploy, so there is no poll and a long staleTime.
 */
export interface UseCodingModelsResult {
  data: CodingModelsResponse | undefined
  loading: boolean
  error: string | null
}

const fetchCodingModels = async (
  customer: string,
  job: string,
): Promise<CodingModelsResponse> => {
  const url =
    `${API_BASE_URL}/autocoder/${encodeURIComponent(customer)}` +
    `/${encodeURIComponent(job)}/models`
  const res = await axios.get<CodingModelsResponse>(url, {
    headers: { Accept: 'application/json' },
  })
  return res.data
}

export const useCodingModels = (
  customer: string,
  job: string,
  enabled = true,
): UseCodingModelsResult => {
  const account = useAccount()

  const query = useQuery<CodingModelsResponse, Error>({
    // House key shape: ['autocoder', customer, job, <resource>].
    queryKey: ['autocoder', customer, job, 'models'],
    queryFn: () => fetchCodingModels(customer, job),
    enabled: enabled && !!customer && !!job && !!account.user,
    staleTime: 5 * 60 * 1000,
  })

  return {
    data: query.data,
    loading: query.isLoading,
    error: query.error ? query.error.message : null,
  }
}

export default useCodingModels
